import type { ComponentTemplate } from "@v1/types/component-template";
import type { DragType } from "@v1/types/drag-type";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { useSetCandidateNode } from "@v1/components/canvas/candidateNode";
import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import useCurrentDragType from "@v1/hooks/useCurrentDragType";
import useDragAndDrop from "@v1/providers/DragAndDropProvider";
import { selectCandidateComponent } from "@v1/reducers/candidate-reducer";
import {
  selectComponentDataMapping,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  selectGetAttribute,
} from "@v1/reducers/core-reducer";
import { setDropTarget } from "@v1/reducers/drag-and-drop-reducer";
import { useEditorStore } from "@v1/store/store";
import { getTemplatePreviewImageSrc } from "@v1/utils/component-template";
import { processDropTargetDrag } from "@v1/utils/dropTarget";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import isEqual from "lodash-es/isEqual";

const ComponentTemplateDragOverlay: React.FC<
  React.PropsWithChildren<{
    showOutlineOnHover?: boolean;
    identifier: string;
    template: ComponentTemplate;
    dragType: DragType;
    scope?: "left-bar" | "store";
  }>
> = ({ template, scope, showOutlineOnHover = true, dragType, identifier }) => {
  const store = useEditorStore();
  const { onDrop } = useDragAndDrop();
  const logEvent = useLogAnalytics();

  const { currentDragType, setCurrentDragTypeAndIdentifier } =
    useCurrentDragType();
  const { setCandidateNodeFromDragging } = useSetCandidateNode();

  const divRef = React.useRef<HTMLDivElement>(null);
  const dragStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const ghostRef = React.useRef<HTMLDivElement | null>(null);
  const cleanupRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    // NOTE (Patrick, 2026-03-03): Only handle left-click (button 0) to avoid
    // initiating drag on right-click or middle-click
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    const rect = divRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setCurrentDragTypeAndIdentifier(dragType, identifier);
    document.body.style.userSelect = "none";

    // Remove any lingering ghost from an interrupted drag
    ghostRef.current?.remove();

    const ghost = document.createElement("div");
    Object.assign(ghost.style, {
      position: "fixed",
      width: "150px",
      aspectRatio: "5 / 4",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "6px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
      pointerEvents: "none",
      zIndex: "2147483647",
      opacity: "0.85",
      transition: "opacity 150ms ease",
      left: `${e.clientX - 75}px`,
      top: `${e.clientY - 60}px`,
      overflow: "hidden",
    });

    const imageSrc = getTemplatePreviewImageSrc(template);
    if (imageSrc) {
      Object.assign(ghost.style, {
        background: "var(--color-neutral-soft)",
        padding: "8px",
      });
      const img = document.createElement("img");
      img.src = imageSrc;
      Object.assign(img.style, {
        maxWidth: "100%",
        maxHeight: "100%",
        display: "block",
        objectFit: "contain",
      });
      ghost.append(img);
    } else {
      Object.assign(ghost.style, {
        background: "#e5e7eb",
        padding: "12px",
        fontSize: "12px",
        color: "#374151",
      });
      ghost.textContent = template.name;
    }

    document.body.append(ghost);
    ghostRef.current = ghost;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (ghostRef.current) {
        ghostRef.current.style.left = `${moveEvent.clientX - 75}px`;
        ghostRef.current.style.top = `${moveEvent.clientY - 60}px`;
      }

      const candidateNode = setCandidateNodeFromDragging(moveEvent);

      if (candidateNode) {
        const { clientX: x, clientY: y } = moveEvent;
        const state = store.getState();
        const currentDropTarget = state.dragAndDrop.dropTarget;
        const newDropTarget = processDropTargetDrag(
          { x, y },
          candidateNode,
          { type: "template", template },
          selectCandidateComponent(state)!,
          currentDropTarget,
          selectDraftElement_warningThisWillRerenderOnEveryUpdate(state)!,
          selectGetAttribute(state),
          selectComponentDataMapping(state),
          selectActiveCanvas(state),
          dragType,
        );

        if (newDropTarget && !isEqual(currentDropTarget, newDropTarget)) {
          store.dispatch(setDropTarget(newDropTarget));
        } else if (!newDropTarget) {
          store.dispatch(
            setDropTarget({ componentId: null, edge: null, error: null }),
          );
        }
      } else {
        store.dispatch(
          setDropTarget({ componentId: null, edge: null, error: null }),
        );
      }

      // Dim ghost when over a valid drop target
      if (ghostRef.current) {
        const dropTarget = store.getState().dragAndDrop.dropTarget;
        ghostRef.current.style.opacity = dropTarget.componentId
          ? "0.4"
          : "0.85";
      }
    };

    const handleMouseUp = () => {
      ghostRef.current?.remove();
      ghostRef.current = null;
      dragStartRef.current = null;
      document.body.style.userSelect = "";

      const currentDropTarget = store.getState().dragAndDrop.dropTarget;
      if (currentDropTarget.componentId) {
        logEvent("editor.componentTemplate.used", {
          componentTemplateId: template.id,
          collectionId: template.collectionId,
          categoryId: template.categoryId,
          componentTemplateName: template.name,
          componentTemplateType: template.type,
          componentScope: template.scope,
          source: scope === "left-bar" ? "insert-pane" : "marketplace",
        });
      }

      onDrop?.current?.(currentDropTarget, template, [], scope === "store");
      store.dispatch(
        setDropTarget({ ...currentDropTarget, componentId: null }),
      );
      setCurrentDragTypeAndIdentifier(null, null);

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      cleanupRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    cleanupRef.current = () => {
      ghostRef.current?.remove();
      ghostRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      store.dispatch(
        setDropTarget({ componentId: null, edge: null, error: null }),
      );
      setCurrentDragTypeAndIdentifier(null, null);
    };
  };

  return (
    <div
      ref={divRef}
      onMouseDown={handleMouseDown}
      className={twMerge(
        // NOTE (Sebas, 2024-06-05): The visible class is needed to make the
        // draggable component visible on the screen when dragging it. This is
        // because we are hiding the parent component of the draggable component
        // when dragging.
        "border-0.5 visible absolute left-0 top-0 z-[2147483647] h-full w-full rounded-sm border border-transparent",
        showOutlineOnHover && !currentDragType && "hover:border-primary",
      )}
      style={{ cursor: currentDragType ? "grabbing" : "grab" }}
    />
  );
};

export default ComponentTemplateDragOverlay;
